import React, { useState } from "react";
import {
  Card,
  Input,
  Space,
  Button,
  message,
  Spin,
  Alert,
  Descriptions,
  Timeline,
  Divider,
  Typography,
  Collapse,
  Table,
  Radio,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

// API Configuration
const MORALIS_API_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6IjI1MDk1ZmExLTcxYjAtNDFjZC05NTI2LTAwYWYxMzE5MmRlMiIsIm9yZ0lkIjoiNDU5NjA5IiwidXNlcklkIjoiNDcyODU0IiwidHlwZUlkIjoiNTZlN2ExYzgtYTkwNC00YjNhLWI1NzgtNzk0NjE3ZjdhOWY2IiwidHlwZSI6IlBST0pFQ1QiLCJpYXQiOjE3NTI2NTgzMDAsImV4cCI6NDkwODQxODMwMH0.jS2uJhm8qhv12o0Q4o8sSGIw1MphmzRhRvUADB85Mns";
const ETHERSCAN_API_KEY = "MVPP4EJFKZ2D86FGNH21W6R385PK75A29Q";
const TGE_METHOD_ID = "0xc8aa65c1";
const MIN_BNB_AMOUNT = 2; // Minimum BNB amount to filter transactions
const MAIN_TX_COUNT = 30; // Query last 30 transactions for main address
const RELAY_TX_COUNT = 10; // Query last 10 transactions for relay addresses
const REQUEST_DELAY = 500; // Delay between requests to avoid rate limits
const FETCH_TIMEOUT = 15000; // Request timeout

// API Provider types
type APIProvider = "moralis" | "etherscan";

// Types
interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  timeStamp: string;
  input: string;
  blockNumber: string;
}

interface CoreTransaction {
  inflow?: Transaction;
  tge?: Transaction;
  outflow?: Transaction;
}

interface RelayAddress {
  address: string;
  type: "inflow_relay" | "outflow_relay";
  inflowAmount?: number;
  outflowAmount?: number;
  transactions: Transaction[];
  nextHopAddress?: string; // For outflow relay: the address this relay sends funds to
  nextHopAmount?: number; // Amount sent to next hop
  nextHopHash?: string; // Transaction hash to next hop
  nextHopTime?: string; // Timestamp of next hop transaction
  previousHopAddress?: string; // For inflow relay: the address that sent funds to this relay
  previousHopAmount?: number; // Amount received from previous hop
  previousHopHash?: string; // Transaction hash from previous hop
  previousHopTime?: string; // Timestamp of previous hop transaction
}

interface FundFlowReport {
  mainAddress: string;
  coreTransactions: CoreTransaction;
  relayAddresses: RelayAddress[];
  analysisTime: string;
}

// Utility functions
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithTimeout = async (
  url: string,
  options?: RequestInit,
  timeout: number = FETCH_TIMEOUT
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error("Request timeout");
    }
    throw error;
  }
};

// Moralis API: Get address transactions
const getTransactionsFromMoralis = async (
  address: string,
  limit: number = MAIN_TX_COUNT
): Promise<Transaction[]> => {
  const url = `https://deep-index.moralis.io/api/v2/${address}?chain=bsc&limit=${limit}`;

  const response = await fetchWithTimeout(url, {
    headers: {
      'X-API-Key': MORALIS_API_KEY,
      'Accept': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Moralis API error: ${response.status}`);
  }

  const data = await response.json();

  if (!data.result || !Array.isArray(data.result)) {
    throw new Error("Moralis API returned invalid data");
  }

  // Normalize Moralis format to match Etherscan format
  return data.result.map((tx: any) => ({
    hash: tx.hash || tx.transaction_hash || "",
    from: tx.from_address || tx.from || "",
    to: tx.to_address || tx.to || "",
    value: tx.value || "0",
    timeStamp: tx.block_timestamp ? String(Math.floor(new Date(tx.block_timestamp).getTime() / 1000)) : "0",
    input: tx.input || tx.data || "0x",
    blockNumber: tx.block_number ? String(tx.block_number) : "0",
  }));
};

// Etherscan API: Get address transactions
const getTransactionsFromEtherscan = async (
  address: string,
  limit: number = MAIN_TX_COUNT
): Promise<Transaction[]> => {
  const url = `https://api.etherscan.io/v2/api?chainid=56&module=account&action=txlist&address=${address}&startblock=0&endblock=99999999&page=1&offset=${limit}&sort=desc&apikey=${ETHERSCAN_API_KEY}`;

  const response = await fetchWithTimeout(url);

  if (!response.ok) {
    throw new Error(`Etherscan API error: ${response.status}`);
  }

  const data = await response.json();

  if (data.status === "0" && data.message) {
    if (
      data.message.includes("rate limit") ||
      data.message.includes("Max rate") ||
      data.message.includes("temporarily unavailable")
    ) {
      throw new Error("Etherscan API rate limit or temporarily unavailable");
    }
  }

  if (!data.result || !Array.isArray(data.result)) {
    throw new Error("Etherscan API returned invalid data");
  }

  return data.result;
};

// Universal function to get transactions with API fallback
const getTransactions = async (
  address: string,
  limit: number = MAIN_TX_COUNT,
  preferredProvider: APIProvider = "moralis"
): Promise<Transaction[]> => {
  // Try preferred API first
  try {
    if (preferredProvider === "moralis") {
      return await getTransactionsFromMoralis(address, limit);
    } else {
      return await getTransactionsFromEtherscan(address, limit);
    }
  } catch (error: any) {
    console.warn(`${preferredProvider} API failed:`, error.message);

    // Fallback to alternative API
    const fallbackProvider: APIProvider = preferredProvider === "moralis" ? "etherscan" : "moralis";

    try {
      console.log(`Trying fallback API: ${fallbackProvider}`);
      if (fallbackProvider === "moralis") {
        return await getTransactionsFromMoralis(address, limit);
      } else {
        return await getTransactionsFromEtherscan(address, limit);
      }
    } catch (fallbackError: any) {
      console.error(`Both APIs failed. ${preferredProvider}: ${error.message}, ${fallbackProvider}: ${fallbackError.message}`);
      throw new Error(`All APIs failed: ${error.message}, ${fallbackError.message}`);
    }
  }
};

// Convert Wei to BNB
const weiToBNB = (wei: string): number => {
  return parseFloat(wei) / 1e18;
};

// Format BNB amount
const formatBNB = (amount: number): string => {
  return amount.toFixed(4);
};

// Check if address is a relay/transit address
const isRelayAddress = (
  transactions: Transaction[],
  expectedAmount: number
): boolean => {
  // Look for outflow transaction with similar amount (within 0.01 BNB difference)
  const hasMatchingOutflow = transactions.some((tx) => {
    const amount = weiToBNB(tx.value);
    const diff = Math.abs(amount - expectedAmount);
    return diff <= 0.01 && amount > 0;
  });

  return hasMatchingOutflow;
};

// Analyze relay address
const analyzeRelayAddress = async (
  address: string,
  type: "inflow_relay" | "outflow_relay",
  expectedAmount?: number,
  referenceTimestamp?: string, // Reference timestamp to find previous/next hop
  preferredProvider: APIProvider = "moralis"
): Promise<RelayAddress | null> => {
  try {
    const transactions = await getTransactions(address, RELAY_TX_COUNT, preferredProvider);

    if (transactions.length === 0) {
      return null;
    }

    // Calculate total inflow and outflow
    let totalInflow = 0;
    let totalOutflow = 0;

    transactions.forEach((tx) => {
      const amount = weiToBNB(tx.value);
      if (tx.to.toLowerCase() === address.toLowerCase()) {
        totalInflow += amount;
      } else if (tx.from.toLowerCase() === address.toLowerCase()) {
        totalOutflow += amount;
      }
    });

    // Check if this is a relay address
    const isRelay = expectedAmount
      ? isRelayAddress(transactions, expectedAmount)
      : Math.abs(totalInflow - totalOutflow) < 0.1;

    if (!isRelay) {
      return null;
    }

    // Sort transactions by timestamp (descending - newest first)
    const sortedTxs = [...transactions].sort(
      (a, b) => parseInt(b.timeStamp) - parseInt(a.timeStamp)
    );

    let nextHopAddress: string | undefined;
    let nextHopAmount: number | undefined;
    let nextHopHash: string | undefined;
    let nextHopTime: string | undefined;

    let previousHopAddress: string | undefined;
    let previousHopAmount: number | undefined;
    let previousHopHash: string | undefined;
    let previousHopTime: string | undefined;

    if (type === "outflow_relay" && referenceTimestamp) {
      // For outflow relay: find the most recent outflow AFTER the reference time
      const refTime = parseInt(referenceTimestamp);
      const outflowsAfterRef = sortedTxs.filter(tx =>
        tx.from.toLowerCase() === address.toLowerCase() &&
        parseInt(tx.timeStamp) > refTime &&
        weiToBNB(tx.value) > 0.1
      );

      if (outflowsAfterRef.length > 0) {
        // Sort by time descending (most recent first), take the most recent
        outflowsAfterRef.sort((a, b) => parseInt(b.timeStamp) - parseInt(a.timeStamp));
        const nextHop = outflowsAfterRef[0];
        nextHopAddress = nextHop.to;
        nextHopAmount = weiToBNB(nextHop.value);
        nextHopHash = nextHop.hash;
        nextHopTime = nextHop.timeStamp;
      }
    } else if (type === "inflow_relay" && referenceTimestamp) {
      // For inflow relay: find the most recent inflow BEFORE the reference time
      const refTime = parseInt(referenceTimestamp);
      const inflowsBeforeRef = sortedTxs.filter(tx =>
        tx.to.toLowerCase() === address.toLowerCase() &&
        parseInt(tx.timeStamp) < refTime &&
        weiToBNB(tx.value) > 0.1
      );

      if (inflowsBeforeRef.length > 0) {
        // Sort by time descending (most recent first), take the most recent
        inflowsBeforeRef.sort((a, b) => parseInt(b.timeStamp) - parseInt(a.timeStamp));
        const prevHop = inflowsBeforeRef[0];
        previousHopAddress = prevHop.from;
        previousHopAmount = weiToBNB(prevHop.value);
        previousHopHash = prevHop.hash;
        previousHopTime = prevHop.timeStamp;
      }
    } else {
      // Fallback: find any significant transaction (for backward compatibility)
      for (const tx of sortedTxs) {
        const amount = weiToBNB(tx.value);
        if (amount <= 0.1) continue;

        if (type === "outflow_relay" && tx.from.toLowerCase() === address.toLowerCase()) {
          nextHopAddress = tx.to;
          nextHopAmount = amount;
          nextHopHash = tx.hash;
          nextHopTime = tx.timeStamp;
          break;
        } else if (type === "inflow_relay" && tx.to.toLowerCase() === address.toLowerCase()) {
          previousHopAddress = tx.from;
          previousHopAmount = amount;
          previousHopHash = tx.hash;
          previousHopTime = tx.timeStamp;
          break;
        }
      }
    }

    return {
      address,
      type,
      inflowAmount: totalInflow,
      outflowAmount: totalOutflow,
      transactions,
      nextHopAddress,
      nextHopAmount,
      nextHopHash,
      nextHopTime,
      previousHopAddress,
      previousHopAmount,
      previousHopHash,
      previousHopTime,
    };
  } catch (error) {
    console.error(`Failed to analyze relay address ${address}:`, error);
    return null;
  }
};

const FundFlowTracker: React.FC = () => {
  const [inputAddress, setInputAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<FundFlowReport | null>(null);
  const [apiProvider, setApiProvider] = useState<APIProvider>("moralis");

  const analyzeFundFlow = async () => {
    const address = inputAddress.trim();

    if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
      message.error("Please enter a valid wallet address");
      return;
    }

    setLoading(true);
    setReport(null);

    try {
      // Step 1: Get last 30 transactions
      message.info("Fetching transactions...");
      const allTransactions = await getTransactions(address, MAIN_TX_COUNT, apiProvider);
      await delay(REQUEST_DELAY);

      if (allTransactions.length === 0) {
        message.warning("No transactions found for this address");
        setLoading(false);
        return;
      }

      // Step 2: Filter transactions with amount > 2 BNB
      const filteredTransactions = allTransactions.filter((tx) => {
        const amount = weiToBNB(tx.value);
        return amount >= MIN_BNB_AMOUNT;
      });

      if (filteredTransactions.length === 0) {
        message.warning(
          `No transactions found with amount > ${MIN_BNB_AMOUNT} BNB`
        );
        setLoading(false);
        return;
      }

      // Step 3: Identify core transactions (use most recent transactions)
      message.info("Identifying core transactions...");
      const coreTransactions: CoreTransaction = {};

      // Sort by timestamp (descending - newest first)
      const sortedByTimeDesc = [...filteredTransactions].sort(
        (a, b) => parseInt(b.timeStamp) - parseInt(a.timeStamp)
      );

      // Find most recent TGE participation transaction (as anchor point)
      const tgeTx = sortedByTimeDesc.find((tx) =>
        tx.input.startsWith(TGE_METHOD_ID)
      );

      if (!tgeTx) {
        message.warning("No TGE participation transaction found");
        setLoading(false);
        return;
      }

      const tgeTime = parseInt(tgeTx.timeStamp);

      // Find most recent inflow BEFORE TGE (to = main address, time < TGE time)
      const inflowTx = sortedByTimeDesc.find(
        (tx) =>
          tx.to.toLowerCase() === address.toLowerCase() &&
          parseInt(tx.timeStamp) < tgeTime
      );

      // Find most recent outflow AFTER TGE (from = main address, not TGE, time > TGE time)
      const outflowTx = sortedByTimeDesc.find(
        (tx) =>
          tx.from.toLowerCase() === address.toLowerCase() &&
          !tx.input.startsWith(TGE_METHOD_ID) &&
          parseInt(tx.timeStamp) > tgeTime
      );

      coreTransactions.inflow = inflowTx;
      coreTransactions.tge = tgeTx;
      coreTransactions.outflow = outflowTx;

      // Step 4: Analyze relay addresses
      message.info("Analyzing relay addresses...");
      const relayAddresses: RelayAddress[] = [];

      // Analyze inflow source address (with reference timestamp)
      if (inflowTx) {
        await delay(REQUEST_DELAY);
        const inflowAmount = weiToBNB(inflowTx.value);
        const inflowRelay = await analyzeRelayAddress(
          inflowTx.from,
          "inflow_relay",
          inflowAmount,
          inflowTx.timeStamp, // Pass reference timestamp
          apiProvider
        );
        if (inflowRelay) {
          relayAddresses.push(inflowRelay);
        }
      }

      // Analyze outflow destination address (with reference timestamp)
      if (outflowTx) {
        await delay(REQUEST_DELAY);
        const outflowAmount = weiToBNB(outflowTx.value);
        const outflowRelay = await analyzeRelayAddress(
          outflowTx.to,
          "outflow_relay",
          outflowAmount,
          outflowTx.timeStamp, // Pass reference timestamp
          apiProvider
        );
        if (outflowRelay) {
          relayAddresses.push(outflowRelay);
        }
      }

      // Generate report
      const fundFlowReport: FundFlowReport = {
        mainAddress: address,
        coreTransactions,
        relayAddresses,
        analysisTime: new Date().toLocaleString(),
      };

      setReport(fundFlowReport);
      message.success("Analysis completed!");
    } catch (error: any) {
      console.error("Analysis failed:", error);
      message.error(`Analysis failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    if (!report) {
      message.warning("No report to export");
      return;
    }

    const { mainAddress, coreTransactions, relayAddresses, analysisTime } =
      report;

    // Generate markdown report
    let markdown = `# Fund Flow Tracking Report\n\n`;
    markdown += `**Analysis Time:** ${analysisTime}\n\n`;
    markdown += `**Target Address:** ${mainAddress}\n\n`;
    markdown += `---\n\n`;

    markdown += `## Role Assignment\n\n`;
    markdown += `| Role | Address | Description |\n`;
    markdown += `|------|---------|-------------|\n`;

    // Check for inflow relay and its previous hop
    const inflowRelay = coreTransactions.inflow
      ? relayAddresses.find(
          (r) =>
            r.type === "inflow_relay" &&
            r.address.toLowerCase() ===
              coreTransactions.inflow!.from.toLowerCase()
        )
      : undefined;

    // Initial source (if inflow relay has previous hop)
    if (inflowRelay && inflowRelay.previousHopAddress) {
      markdown += `| Initial Source | ${inflowRelay.previousHopAddress} | Fund flow starting point |\n`;
      markdown += `| Inflow Relay Address | ${inflowRelay.address} | Relay address for fund distribution |\n`;
    } else if (coreTransactions.inflow) {
      markdown += `| Initial Source | ${coreTransactions.inflow.from} | Fund flow starting point |\n`;
    }

    markdown += `| Activity Participation Address | ${mainAddress} | Binance Web3 address, participated in TGE |\n`;

    // Check for outflow relay and its next hop
    const outflowRelay = coreTransactions.outflow
      ? relayAddresses.find(
          (r) =>
            r.type === "outflow_relay" &&
            r.address.toLowerCase() ===
              coreTransactions.outflow!.to.toLowerCase()
        )
      : undefined;

    // Outflow destination
    if (outflowRelay) {
      markdown += `| Outflow Relay Address | ${outflowRelay.address} | Relay address for fund collection |\n`;
      if (outflowRelay.nextHopAddress) {
        markdown += `| Final Collection | ${outflowRelay.nextHopAddress} | Final fund destination |\n`;
      }
    } else if (coreTransactions.outflow) {
      markdown += `| Final Collection | ${coreTransactions.outflow.to} | Final fund destination |\n`;
    }

    markdown += `\n---\n\n`;

    markdown += `## Transaction Details\n\n`;
    markdown += `| Step | From Address | To Address | Amount | Transaction Hash | Description |\n`;
    markdown += `|------|--------------|------------|--------|------------------|-------------|\n`;

    let step = 0;

    // Step 0: Initial Source -> Inflow Relay (if exists)
    if (inflowRelay && inflowRelay.previousHopAddress) {
      const amount = formatBNB(inflowRelay.previousHopAmount || 0);
      markdown += `| ${step++} (Initial) | ${inflowRelay.previousHopAddress} | ${inflowRelay.address} | ${amount} BNB | ${inflowRelay.previousHopHash} | Funds sent from initial source to distribution relay |\n`;
    }

    // Step 1: Inflow transaction
    if (coreTransactions.inflow) {
      const amount = formatBNB(weiToBNB(coreTransactions.inflow.value));
      const desc = inflowRelay
        ? "Funds transferred from relay to Binance Web3 wallet"
        : "Funds transferred into Binance Web3 wallet";
      markdown += `| ${step++} (Inflow) | ${coreTransactions.inflow.from} | ${coreTransactions.inflow.to} | ${amount} BNB | ${coreTransactions.inflow.hash} | ${desc} |\n`;
    }

    // Step 2: TGE transaction
    if (coreTransactions.tge) {
      const amount = formatBNB(weiToBNB(coreTransactions.tge.value));
      markdown += `| ${step++} (TGE) | ${coreTransactions.tge.from} | ${coreTransactions.tge.to} | ${amount} BNB | ${coreTransactions.tge.hash} | Participated in TGE activity |\n`;
    }

    // Step 3: Outflow transaction
    if (coreTransactions.outflow) {
      const amount = formatBNB(weiToBNB(coreTransactions.outflow.value));
      const desc = outflowRelay
        ? "Funds transferred from Web3 wallet to collection relay"
        : "Remaining funds transferred out";
      markdown += `| ${step++} (Outflow) | ${coreTransactions.outflow.from} | ${coreTransactions.outflow.to} | ${amount} BNB | ${coreTransactions.outflow.hash} | ${desc} |\n`;

      // Step 4: Final collection (if outflow relay has next hop)
      if (outflowRelay && outflowRelay.nextHopAddress) {
        const collectionAmount = formatBNB(outflowRelay.nextHopAmount || 0);
        markdown += `| ${step++} (Collection) | ${outflowRelay.address} | ${outflowRelay.nextHopAddress} | ${collectionAmount} BNB | ${outflowRelay.nextHopHash} | Funds collected from relay to final destination |\n`;
      }
    }

    markdown += `\n---\n\n`;

    // Relay addresses section
    if (relayAddresses.length > 0) {
      markdown += `## Identified Relay Addresses\n\n`;
      relayAddresses.forEach((relay) => {
        markdown += `### ${relay.type === "inflow_relay" ? "Inflow Relay" : "Outflow Relay"}: ${relay.address}\n\n`;
        markdown += `- **Total Inflow:** ${formatBNB(relay.inflowAmount || 0)} BNB\n`;
        markdown += `- **Total Outflow:** ${formatBNB(relay.outflowAmount || 0)} BNB\n`;

        if (relay.nextHopAddress) {
          markdown += `- **Next Hop Address:** ${relay.nextHopAddress}\n`;
          markdown += `- **Next Hop Amount:** ${formatBNB(relay.nextHopAmount || 0)} BNB\n`;
          markdown += `- **Next Hop Transaction Hash:** ${relay.nextHopHash}\n`;
        }

        markdown += `- **Recent Transactions:** ${relay.transactions.length}\n\n`;
      });
    }

    // Download as markdown file
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fund_flow_report_${mainAddress.slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);

    message.success("Report exported successfully!");
  };

  const copyRelayTransactions = (relay: RelayAddress) => {
    const lines = relay.transactions
      .filter((tx) => weiToBNB(tx.value) >= MIN_BNB_AMOUNT)
      .map((tx) => {
        const amount = formatBNB(weiToBNB(tx.value));
        return `${tx.from} -> ${tx.to} ${amount}BNB ${tx.hash}`;
      });

    if (lines.length === 0) {
      message.warning(`No transactions >= ${MIN_BNB_AMOUNT} BNB to copy`);
      return;
    }

    navigator.clipboard
      .writeText(lines.join("\n"))
      .then(() => message.success(`Copied ${lines.length} transactions`))
      .catch(() => message.error("Copy failed"));
  };

  const copyCompleteFlowChain = () => {
    if (!report) {
      message.warning("No report to copy");
      return;
    }

    const { coreTransactions, relayAddresses } = report;

    const inflowRelay = coreTransactions.inflow
      ? relayAddresses.find(
          (r) =>
            r.type === "inflow_relay" &&
            r.address.toLowerCase() === coreTransactions.inflow!.from.toLowerCase()
        )
      : undefined;

    const outflowRelay = coreTransactions.outflow
      ? relayAddresses.find(
          (r) =>
            r.type === "outflow_relay" &&
            r.address.toLowerCase() === coreTransactions.outflow!.to.toLowerCase()
        )
      : undefined;

    const lines: string[] = [];

    if (inflowRelay && inflowRelay.previousHopAddress && inflowRelay.previousHopHash) {
      const amt = formatBNB(inflowRelay.previousHopAmount || 0);
      lines.push(`${inflowRelay.previousHopAddress} -> ${inflowRelay.address} ${amt}BNB ${inflowRelay.previousHopHash}`);
    }

    if (coreTransactions.inflow) {
      const amt = formatBNB(weiToBNB(coreTransactions.inflow.value));
      lines.push(`${coreTransactions.inflow.from} -> ${coreTransactions.inflow.to} ${amt}BNB ${coreTransactions.inflow.hash}`);
    }

    if (coreTransactions.tge) {
      const amt = formatBNB(weiToBNB(coreTransactions.tge.value));
      lines.push(`${coreTransactions.tge.from} -> ${coreTransactions.tge.to} ${amt}BNB ${coreTransactions.tge.hash}`);
    }

    if (coreTransactions.outflow) {
      const amt = formatBNB(weiToBNB(coreTransactions.outflow.value));
      lines.push(`${coreTransactions.outflow.from} -> ${coreTransactions.outflow.to} ${amt}BNB ${coreTransactions.outflow.hash}`);
    }

    if (outflowRelay && outflowRelay.nextHopAddress && outflowRelay.nextHopHash) {
      const amt = formatBNB(outflowRelay.nextHopAmount || 0);
      lines.push(`${outflowRelay.address} -> ${outflowRelay.nextHopAddress} ${amt}BNB ${outflowRelay.nextHopHash}`);
    }

    if (lines.length === 0) {
      message.warning("No chain transactions to copy");
      return;
    }

    navigator.clipboard
      .writeText(lines.join("\n"))
      .then(() => message.success(`Copied ${lines.length} chain steps`))
      .catch(() => message.error("Copy failed"));
  };

  const renderReport = () => {
    if (!report) return null;

    const { mainAddress, coreTransactions, relayAddresses } = report;

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Alert
          message="Analysis Completed"
          description={`Successfully analyzed fund flow for address: ${mainAddress}`}
          type="success"
          showIcon
        />

        <Card
          title="Complete Fund Flow Chain"
          size="small"
          extra={
            <Button size="small" icon={<CopyOutlined />} onClick={copyCompleteFlowChain}>
              复制资金流(文本)
            </Button>
          }
        >
          <Timeline>
            {/* Step 0: Initial Source -> Inflow Relay (if exists) */}
            {coreTransactions.inflow && (() => {
              const inflowRelay = relayAddresses.find(
                (r) =>
                  r.type === "inflow_relay" &&
                  r.address.toLowerCase() === coreTransactions.inflow!.from.toLowerCase()
              );

              if (inflowRelay && inflowRelay.previousHopAddress) {
                return (
                  <Timeline.Item color="cyan">
                    <Title level={5}>
                      0. Initial Source to Distribution Relay
                    </Title>
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="From (Initial Source)">
                        <a
                          href={`https://bscscan.com/address/${inflowRelay.previousHopAddress}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {inflowRelay.previousHopAddress}
                        </a>
                      </Descriptions.Item>
                      <Descriptions.Item label="To (Inflow Relay)">
                        {inflowRelay.address}
                      </Descriptions.Item>
                      <Descriptions.Item label="Amount">
                        {formatBNB(inflowRelay.previousHopAmount || 0)} BNB
                      </Descriptions.Item>
                      <Descriptions.Item label="Hash">
                        <a
                          href={`https://bscscan.com/tx/${inflowRelay.previousHopHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {inflowRelay.previousHopHash}
                        </a>
                      </Descriptions.Item>
                      <Descriptions.Item label="Time">
                        {new Date(
                          parseInt(inflowRelay.previousHopTime || "0") * 1000
                        ).toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Note">
                        <Text type="secondary">
                          Funds sent from initial source to relay address
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </Timeline.Item>
                );
              }
              return null;
            })()}

            {/* Step 1: Inflow to Web3 Wallet */}
            {coreTransactions.inflow && (() => {
              const inflowRelay = relayAddresses.find(
                (r) =>
                  r.type === "inflow_relay" &&
                  r.address.toLowerCase() === coreTransactions.inflow!.from.toLowerCase()
              );

              const hasInflowRelay = !!inflowRelay;
              const stepNumber = inflowRelay && inflowRelay.previousHopAddress ? 1 : 1;

              return (
                <Timeline.Item color="blue">
                  <Title level={5}>
                    {stepNumber}. {hasInflowRelay ? "Inflow from Distribution Relay" : "Inflow Transaction (Fund Distribution)"}
                  </Title>
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="From">
                      {coreTransactions.inflow.from}
                    </Descriptions.Item>
                    <Descriptions.Item label="To (Web3 Wallet)">
                      {coreTransactions.inflow.to}
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount">
                      {formatBNB(weiToBNB(coreTransactions.inflow.value))} BNB
                    </Descriptions.Item>
                    <Descriptions.Item label="Hash">
                      <a
                        href={`https://bscscan.com/tx/${coreTransactions.inflow.hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {coreTransactions.inflow.hash}
                      </a>
                    </Descriptions.Item>
                    <Descriptions.Item label="Time">
                      {new Date(
                        parseInt(coreTransactions.inflow.timeStamp) * 1000
                      ).toLocaleString()}
                    </Descriptions.Item>
                    {hasInflowRelay && (
                      <Descriptions.Item label="Note">
                        <Text type="warning">
                          Source address is identified as a relay/distribution address
                        </Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Timeline.Item>
              );
            })()}

            {/* Step 2: TGE Participation */}
            {coreTransactions.tge && (() => {
              const inflowRelay = relayAddresses.find((r) => r.type === "inflow_relay");
              const hasInflowPrevHop = inflowRelay && inflowRelay.previousHopAddress;
              const stepNumber = hasInflowPrevHop ? 2 : 2;

              return (
                <Timeline.Item color="green">
                  <Title level={5}>{stepNumber}. TGE Participation Transaction</Title>
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="From (Web3 Wallet)">
                      {coreTransactions.tge.from}
                    </Descriptions.Item>
                    <Descriptions.Item label="To (TGE Contract)">
                      {coreTransactions.tge.to}
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount">
                      {formatBNB(weiToBNB(coreTransactions.tge.value))} BNB
                    </Descriptions.Item>
                    <Descriptions.Item label="Hash">
                      <a
                        href={`https://bscscan.com/tx/${coreTransactions.tge.hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {coreTransactions.tge.hash}
                      </a>
                    </Descriptions.Item>
                    <Descriptions.Item label="Method ID">
                      <Text code>{TGE_METHOD_ID}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Time">
                      {new Date(
                        parseInt(coreTransactions.tge.timeStamp) * 1000
                      ).toLocaleString()}
                    </Descriptions.Item>
                  </Descriptions>
                </Timeline.Item>
              );
            })()}

            {/* Step 3: Outflow from Web3 Wallet */}
            {coreTransactions.outflow && (() => {
              const inflowRelay = relayAddresses.find((r) => r.type === "inflow_relay");
              const hasInflowPrevHop = inflowRelay && inflowRelay.previousHopAddress;

              const outflowRelay = relayAddresses.find(
                (r) =>
                  r.type === "outflow_relay" &&
                  r.address.toLowerCase() === coreTransactions.outflow!.to.toLowerCase()
              );

              const hasOutflowRelay = !!outflowRelay;
              const stepNumber = hasInflowPrevHop ? 3 : 3;

              return (
                <>
                  <Timeline.Item color="orange">
                    <Title level={5}>
                      {stepNumber}. {hasOutflowRelay ? "Outflow to Collection Relay" : "Outflow Transaction (Fund Collection)"}
                    </Title>
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="From (Web3 Wallet)">
                        {coreTransactions.outflow.from}
                      </Descriptions.Item>
                      <Descriptions.Item label="To">
                        {coreTransactions.outflow.to}
                      </Descriptions.Item>
                      <Descriptions.Item label="Amount">
                        {formatBNB(weiToBNB(coreTransactions.outflow.value))} BNB
                      </Descriptions.Item>
                      <Descriptions.Item label="Hash">
                        <a
                          href={`https://bscscan.com/tx/${coreTransactions.outflow.hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {coreTransactions.outflow.hash}
                        </a>
                      </Descriptions.Item>
                      <Descriptions.Item label="Time">
                        {new Date(
                          parseInt(coreTransactions.outflow.timeStamp) * 1000
                        ).toLocaleString()}
                      </Descriptions.Item>
                      {hasOutflowRelay && (
                        <Descriptions.Item label="Note">
                          <Text type="warning">
                            Destination address is identified as a relay/transit address
                          </Text>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Timeline.Item>

                  {/* Step 4: Final Collection (if outflow relay exists) */}
                  {outflowRelay && outflowRelay.nextHopAddress && (
                    <Timeline.Item color="purple">
                      <Title level={5}>{stepNumber + 1}. Final Collection Transaction</Title>
                      <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="From (Collection Relay)">
                          {outflowRelay.address}
                        </Descriptions.Item>
                        <Descriptions.Item label="To (Final Destination)">
                          <a
                            href={`https://bscscan.com/address/${outflowRelay.nextHopAddress}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {outflowRelay.nextHopAddress}
                          </a>
                        </Descriptions.Item>
                        <Descriptions.Item label="Amount">
                          {formatBNB(outflowRelay.nextHopAmount || 0)} BNB
                        </Descriptions.Item>
                        <Descriptions.Item label="Hash">
                          <a
                            href={`https://bscscan.com/tx/${outflowRelay.nextHopHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {outflowRelay.nextHopHash}
                          </a>
                        </Descriptions.Item>
                        <Descriptions.Item label="Time">
                          {new Date(
                            parseInt(outflowRelay.nextHopTime || "0") * 1000
                          ).toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Note">
                          <Text type="success">
                            Funds collected from relay to final destination address
                          </Text>
                        </Descriptions.Item>
                      </Descriptions>
                    </Timeline.Item>
                  )}
                </>
              );
            })()}
          </Timeline>

          {!coreTransactions.inflow &&
            !coreTransactions.tge &&
            !coreTransactions.outflow && (
              <Alert
                message="No core transactions found"
                description="Unable to identify the expected transaction pattern (inflow → TGE → outflow)"
                type="warning"
                showIcon
              />
            )}
        </Card>

        {relayAddresses.length > 0 && (
          <Card title="Identified Relay Addresses" size="small">
            {relayAddresses.map((relay, index) => (
              <div key={index} style={{ marginBottom: 16 }}>
                <Divider orientation="left">
                  {relay.type === "inflow_relay"
                    ? "Inflow Relay Address"
                    : "Outflow Relay Address"}
                </Divider>
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="Address" span={2}>
                    {relay.address}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Inflow">
                    {formatBNB(relay.inflowAmount || 0)} BNB
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Outflow">
                    {formatBNB(relay.outflowAmount || 0)} BNB
                  </Descriptions.Item>
                  {relay.nextHopAddress && (
                    <>
                      <Descriptions.Item label="Next Hop Address" span={2}>
                        <a
                          href={`https://bscscan.com/address/${relay.nextHopAddress}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {relay.nextHopAddress}
                        </a>
                      </Descriptions.Item>
                      <Descriptions.Item label="Next Hop Amount">
                        {formatBNB(relay.nextHopAmount || 0)} BNB
                      </Descriptions.Item>
                      <Descriptions.Item label="Next Hop Hash">
                        <a
                          href={`https://bscscan.com/tx/${relay.nextHopHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {relay.nextHopHash?.slice(0, 10)}...{relay.nextHopHash?.slice(-8)}
                        </a>
                      </Descriptions.Item>
                    </>
                  )}
                  <Descriptions.Item label="Recent Transactions" span={2}>
                    {relay.transactions.length} transactions
                  </Descriptions.Item>
                </Descriptions>

                <Collapse style={{ marginTop: 8 }}>
                  <Panel
                    header="View Transaction Details"
                    key="1"
                    extra={
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyRelayTransactions(relay);
                        }}
                      >
                        复制资金流(文本)
                      </Button>
                    }
                  >
                    <Table
                      dataSource={relay.transactions
                        .filter((tx) => weiToBNB(tx.value) >= MIN_BNB_AMOUNT)
                        .map((tx, i) => ({
                          key: i,
                          ...tx,
                        }))}
                      columns={[
                        {
                          title: "Direction",
                          dataIndex: "from",
                          key: "direction",
                          render: (from: string) =>
                            from.toLowerCase() === relay.address.toLowerCase()
                              ? "Outflow"
                              : "Inflow",
                        },
                        {
                          title: "From",
                          dataIndex: "from",
                          key: "from",
                          ellipsis: true,
                          render: (from: string) => (
                            <a
                              href={`https://bscscan.com/address/${from}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {from.slice(0, 8)}...{from.slice(-6)}
                            </a>
                          ),
                        },
                        {
                          title: "To",
                          dataIndex: "to",
                          key: "to",
                          ellipsis: true,
                          render: (to: string) => (
                            <a
                              href={`https://bscscan.com/address/${to}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {to.slice(0, 8)}...{to.slice(-6)}
                            </a>
                          ),
                        },
                        {
                          title: "Amount (BNB)",
                          dataIndex: "value",
                          key: "value",
                          render: (value: string) => formatBNB(weiToBNB(value)),
                        },
                        {
                          title: "Hash",
                          dataIndex: "hash",
                          key: "hash",
                          render: (hash: string) => (
                            <a
                              href={`https://bscscan.com/tx/${hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {hash.slice(0, 10)}...{hash.slice(-8)}
                            </a>
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                      scroll={{ x: true }}
                    />
                  </Panel>
                </Collapse>
              </div>
            ))}
          </Card>
        )}

        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={exportReport}
          block
        >
          Export Report (Markdown)
        </Button>
      </Space>
    );
  };

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined />
          Fund Flow Tracker (TGE Case Analysis)
        </Space>
      }
      extra={
        <Button
          type="link"
          icon={<InfoCircleOutlined />}
          href="https://bscscan.com"
          target="_blank"
        >
          BSCScan
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Alert
          message="About This Tool"
          description="This tool analyzes fund flow patterns for Binance Web3 wallets participating in TGE activities. It identifies core transactions (inflow, TGE participation, outflow) and detects relay addresses used to obscure fund origins and destinations. The tool presents a complete 4-step fund flow chain when relay addresses are detected."
          type="info"
          showIcon
          closable
        />

        {/* API Provider Selection */}
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>API Provider:</div>
          <Radio.Group
            value={apiProvider}
            onChange={(e) => setApiProvider(e.target.value)}
            disabled={loading}
          >
            <Radio.Button value="moralis">Moralis (Recommended)</Radio.Button>
            <Radio.Button value="etherscan">Etherscan (Backup)</Radio.Button>
          </Radio.Group>
          <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
            The system will automatically fallback to the backup API if the primary fails
          </div>
        </div>

        <div>
          <Text strong>Enter Binance Web3 Wallet Address:</Text>
          <Input
            placeholder="0x..."
            value={inputAddress}
            onChange={(e) => setInputAddress(e.target.value)}
            size="large"
            style={{ marginTop: 8 }}
            onPressEnter={analyzeFundFlow}
          />
        </div>

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={analyzeFundFlow}
          loading={loading}
          size="large"
          block
        >
          {loading ? "Analyzing..." : "Start Analysis"}
        </Button>

        <Collapse>
          <Panel header="How It Works" key="1">
            <Paragraph>
              <Text strong>Step 1:</Text> Fetches the last 30 transactions for
              the given address
            </Paragraph>
            <Paragraph>
              <Text strong>Step 2:</Text> Filters transactions with amount
              greater than 2 BNB
            </Paragraph>
            <Paragraph>
              <Text strong>Step 3:</Text> Identifies three core transactions:
              <ul>
                <li>
                  <Text strong>Inflow:</Text> First transaction where funds
                  enter the address
                </li>
                <li>
                  <Text strong>TGE Participation:</Text> Transaction with method
                  ID {TGE_METHOD_ID}
                </li>
                <li>
                  <Text strong>Outflow:</Text> First transaction where funds
                  exit the address
                </li>
              </ul>
            </Paragraph>
            <Paragraph>
              <Text strong>Step 4:</Text> Queries the last 10 transactions for
              both inflow source and outflow destination addresses
            </Paragraph>
            <Paragraph>
              <Text strong>Step 5:</Text> Detects relay addresses by analyzing
              if an address receives X BNB and then transfers approximately X
              BNB (within 0.01 BNB difference)
            </Paragraph>
            <Paragraph>
              <Text strong>Step 6:</Text> Tracks the final destination by
              identifying the next hop from relay addresses (if any)
            </Paragraph>
            <Paragraph>
              <Text strong>Step 7:</Text> Generates a comprehensive 4-step fund
              flow report:
              <ul>
                <li>Step 1: Inflow (Source → Web3 Wallet)</li>
                <li>Step 2: TGE Participation (Web3 Wallet → Contract)</li>
                <li>Step 3: Outflow to Relay (Web3 Wallet → Collection Transit)</li>
                <li>Step 4: Final Collection (Relay → Final Destination) - if relay detected</li>
              </ul>
            </Paragraph>
          </Panel>
        </Collapse>

        {loading && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" tip="Analyzing fund flow patterns..." />
          </div>
        )}

        {!loading && renderReport()}
      </Space>
    </Card>
  );
};

export default FundFlowTracker;
